import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
  retry,
} from '@reduxjs/toolkit/query/react';

import {
  DocumentDetails,
  FeatureFlagsResponse,
  FreshChatGroupsResponse,
  GetDocumentListParams,
  GetDocumentListResponse,
  StandardizedDocType,
} from '../apiInterfaces';
import { SOLVE_V2_BASE_URL } from '../constants';
import defaultHeaders from '../defaultHeaders';
import {
  CurrentUserChannelAccess,
  DashboardPermissions,
  DataExportRequest,
  DataExportResponse,
  DocumentNotSearchableReason,
} from './types';
import _omit from 'lodash/fp/omit';
import { Helpdesk } from 'src/components/app/types';
import { PaidPlanResponse } from 'src/reducers/userReducer/types';
import { SolveWidgetProduct } from 'src/types/types';
import {
  ActiveIntentsResponse,
  ContextVariablesResponse,
  ConversationChannel,
  FredeshdeskGroup,
  FreshChatField,
  FreshdeskFieldsResponse,
  IntercomFieldsResponse,
  ProductMentionField,
  WidgetConfigResponse,
} from 'src/types/workflowBuilderAPITypes';
import { getCognitoToken } from 'src/utils/authUtils';
import {
  applyChannelGrantsToPermissions,
  withAgentBuilderAliases,
} from 'src/utils/channelAccess';
import {
  getSelectedOrganizationForSuperAdmin,
  getUserRole,
  UserRoles,
} from 'src/utils/enums';
import { maybeHandleZendeskAuthFailure } from 'src/utils/zendeskAuth';

export const MAX_RETRIES = 1;

const baseQuery = fetchBaseQuery({
  baseUrl: API_URL,
  // Zendesk build authenticates exclusively via the access-token cookie
  // (credentials: 'include' is the fetchBaseQuery default), so skip bearer
  // and X-ORG-ID entirely there.
  prepareHeaders: async headers => {
    if (!IS_ZENDESK_BUILD) {
      // Add Authorization header with aws-amplify idToken
      try {
        const bearerToken = await getCognitoToken();
        headers.set('Authorization', `Bearer ${bearerToken}`);
      } catch (e) {
        console.error(`Error getting idToken: ${e}`);
      }

      // For superadmin users, add the X-ORG-ID header
      if (getUserRole() === UserRoles.ROLE_SUPER_ADMIN) {
        const selectedOrgId =
          getSelectedOrganizationForSuperAdmin()?.org_id || '';
        headers.set('X-ORG-ID', selectedOrgId);
      }
    }

    const editUuid = defaultHeaders.get('edit-session-uuid');
    if (!!editUuid) {
      headers.set('edit-session-uuid', editUuid);
    }

    return headers;
  },
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: MAX_RETRIES });

// Zendesk build: 401/403 from any RTK query redirects to the proxy sign-in
// page (or, for a suspended-account 401 body, to the tenant origin).
// Standard build: no-op — error flows through to the consumer.
const baseQueryWithZendeskAuthFailure: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQueryWithRetry(args, api, extraOptions);
  if (
    result.error &&
    (result.error.status === 401 || result.error.status === 403)
  ) {
    // fetchBaseQuery defaults to JSON, so the happy path is an object —
    // but a plain text / HTML body (proxy timeout, CORS preflight failure)
    // lands `data` as a string. Guard explicitly before reading `message`.
    const data = result.error.data;
    const message =
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
        ? (data as { message: string }).message
        : undefined;
    maybeHandleZendeskAuthFailure(result.error.status, message);
  }
  return result;
};

export const dashboardApi = createApi({
  baseQuery: baseQueryWithZendeskAuthFailure,
  endpoints: builder => ({
    dataExport: builder.mutation<DataExportResponse, DataExportRequest>({
      query: ({ body }) => {
        return {
          body,
          method: 'POST',
          url: '/data-export',
        };
      },
    }),
    getActiveIntents: builder.query<ActiveIntentsResponse, void>({
      keepUnusedDataFor: 0,
      query: () => SOLVE_V2_BASE_URL + '/active-intents',
    }),
    getChannelAccess: builder.query<CurrentUserChannelAccess, void>({
      query: () => 'dashboard-permissions/channel-access',
    }),
    getContextVariables: builder.query<ContextVariablesResponse, void>({
      providesTags: ['ContextVariables'],
      query: () => SOLVE_V2_BASE_URL + '/workflow-builder/context-variables',
    }),
    getContextVariablesById: builder.query<
      { data: string[] },
      {
        channels?: ConversationChannel[];
        cv_id?: string;
        end: number;
        start: number;
      }
    >({
      query: params => ({
        params,
        url: SOLVE_V2_BASE_URL + '/context-variables',
      }),
    }),
    getDocumentDetails: builder.query<
      DocumentDetails,
      {
        standardizedDocumentId: string;
        standardizedDocumentType: StandardizedDocType;
      }
    >({
      query: ({ standardizedDocumentId, standardizedDocumentType }) => ({
        method: 'GET',
        url: `ssel/connectors/documents/${standardizedDocumentType}/${standardizedDocumentId}`,
      }),
    }),
    getDocumentList: builder.query<
      GetDocumentListResponse,
      GetDocumentListParams
    >({
      forceRefetch: ({ currentArg, previousArg }) => {
        return JSON.stringify(currentArg) !== JSON.stringify(previousArg);
      },
      merge: (currentCacheData, responseData, otherArgs) => {
        if (currentCacheData.cursor === responseData.cursor) {
          return;
        }
        if (otherArgs.arg.cursor) {
          currentCacheData.documents = currentCacheData.documents.concat(
            responseData.documents,
          );
          currentCacheData.cursor = responseData.cursor;
        } else {
          currentCacheData.documents = responseData.documents;
          currentCacheData.cursor = responseData.cursor;
        }
      },
      query: ({
        cursor,
        endCreatedDate,
        integration,
        isPublic,
        sortColumn = 'created_date',
        sortDirection = 'desc',
        sourceId,
        standardizedDocumentType,
        startCreatedDate,
        title,
      }) => {
        const entries = {
          cursor,
          end_created_date: endCreatedDate,
          integration,
          public: isPublic,
          sort_column: sortColumn,
          sort_direction: sortDirection,
          source_id: sourceId,
          start_created_date: startCreatedDate,
          title,
        };
        const params = Object.entries(entries).reduce((prev, current) => {
          const [key, value] = current;
          if (!value) {
            return prev;
          }
          return prev.concat(`${key}=${value}&`);
        }, '');

        return {
          method: 'GET',
          url: `ssel/connectors/documents/${standardizedDocumentType}?${params.slice(
            0,
            -1,
          )}`,
        };
      },
      serializeQueryArgs: ({ queryArgs }) => {
        return _omit(['cursor'], queryArgs);
      },
    }),
    getDocumentNotSearchableReason: builder.query<
      DocumentNotSearchableReason,
      {
        standardizedDocumentId: string;
        standardizedDocumentType: StandardizedDocType;
      }
    >({
      query: ({ standardizedDocumentId, standardizedDocumentType }) => ({
        method: 'GET',
        url: `ssel/connectors/documents/${standardizedDocumentType}/${standardizedDocumentId}/reason`,
      }),
    }),
    getFeatureFlags: builder.query<FeatureFlagsResponse, void>({
      query: () => SOLVE_V2_BASE_URL + '/feature-flags',
    }),
    getFreshchatGroups: builder.query<Array<FredeshdeskGroup>, string>({
      query: (api_url: string) =>
        SOLVE_V2_BASE_URL +
        `/freshchat-groups?api_url=${encodeURIComponent(api_url)}`,
      transformResponse: (response: FreshChatGroupsResponse) => response.groups,
    }),
    getFreshdeskFields: builder.query<Array<FreshChatField>, string>({
      query: (apiUrl: string) =>
        SOLVE_V2_BASE_URL +
        `/freshchat-fields?api_url=${encodeURIComponent(apiUrl)}`,
      transformResponse: (response: FreshdeskFieldsResponse) => response.fields,
    }),
    getHelpdesk: builder.query<{ helpdesk: Helpdesk }, void>({
      query: () => ({ method: 'POST', url: '/helpdesk' }),
    }),
    getIntercomFields: builder.query<IntercomFieldsResponse, void>({
      query: () => SOLVE_V2_BASE_URL + '/intercom-ticket-fields',
    }),
    getPaidPlanDetails: builder.query<PaidPlanResponse, void>({
      query: () => ({ method: 'GET', url: 'get_paid_plan_details' }),
    }),
    getPermissions: builder.query<Record<string, boolean>, void>({
      providesTags: ['ChannelAccess'],
      queryFn: async (_arg, _api, _extraOptions, baseQuery) => {
        const [permissions, channelAccess] = await Promise.all([
          baseQuery({ method: 'POST', url: 'dashboard-applications' }),
          baseQuery('dashboard-permissions/channel-access'),
        ]);
        if (permissions.error) {
          return { error: permissions.error };
        }
        return {
          data: applyChannelGrantsToPermissions(
            withAgentBuilderAliases(
              permissions.data as Record<string, boolean>,
            ),
            channelAccess.data as CurrentUserChannelAccess | undefined,
          ),
        };
      },
    }),
    getPermissionsByRole: builder.query<DashboardPermissions, void>({
      query: () => 'dashboard-permissions',
    }),
    getProductMentionFields: builder.query<
      ProductMentionField[],
      { product: SolveWidgetProduct }
    >({
      query: ({ product }) =>
        SOLVE_V2_BASE_URL + `/workflow-builder/${product}/mention-fields`,
    }),
    getWidgetConfig: builder.query<WidgetConfigResponse, void>({
      query: () => SOLVE_V2_BASE_URL + '/workflow-builder/widget-config',
    }),
    requestPlanTierUpgrade: builder.mutation<void, { message: string }>({
      query: ({ message }) => ({
        body: { message },
        method: 'POST',
        url: 'plan-tier-upgrade-request',
      }),
    }),
  }),
  reducerPath: 'dashboardApi',
  tagTypes: [
    'AIStudioConversations',
    'ChannelAccess',
    'AbTestAIRecommendation',
    'AbTestConfigs',
    'AbTestConfigsWinner',
    'AbTestResultsCharts',
    'ActionUsages',
    'Actions',
    'AgentBuilderFolders',
    'Connectors',
    'CustomFlashbacks',
    'AgentTestAggregateMetrics',
    'AgentTestCases',
    'AgentTestRunResults',
    'AgentTestRuns',
    'AiInstruction',
    'AiInstructions',
    'AiQaConfigs',
    'AiQaSettings',
    'AgentQaConfigs',
    'AgentQaSettings',
    'ArticleContentLabels',
    'AuthConfigs',
    'AutoflowWorkflow',
    'AutoflowVersionHistorySummary',
    'AutopilotWorkflow',
    'Automations',
    'BrandIntents',
    'Brands',
    'BrowserAgentExecution',
    'BrowserAgentExecutions',
    'ContentSnippets',
    'ContextVariableUsages',
    'ContextVariables',
    'ConversationOauthClients',
    'ConversationReview',
    'CustomEventConfigurationUsages',
    'CustomEventConfigurations',
    'EmailSettings',
    'ExternalEventAutomations',
    'FlowDiagram',
    'HandoffConfigurations',
    'ImportedActions',
    'ImprovedPolicyGenerationEligibility',
    'ImprovedPolicyGenerationLatestJob',
    'InsightCards',
    'Intent',
    'IntentTestUsage',
    'IntentTests',
    'IntentUsages',
    'Intents',
    'OrgConfig',
    'OrchestratorWorkflow',
    'OrchestratorAutoflowWorkflow',
    'OrchestratorAutoflowWorkflowNames',
    'OrchestratorRun',
    'OrchestratorRuns',
    'PolicyDiff',
    'PolicyTickets',
    'ProactiveTriggers',
    'Workflow',
    'RedactionProductBlacklist',
    'Simulation',
    'SolveApiConfig',
    'SolveApiToken',
    'Tools',
    'TriageModelDetail',
    'VoiceSettings',
    'WidgetBreakdownMetrics',
    'WorkflowTags',
    'ActionSettings',
    'ActionSecrets',
    'AlertConfig',
    'ErrorEvents',
    'AssistActionClassification',
    'ZendeskActions',
  ],
});

export const {
  useDataExportMutation,
  useGetActiveIntentsQuery,
  useGetChannelAccessQuery,
  useGetContextVariablesByIdQuery,
  useGetContextVariablesQuery,
  useGetDocumentDetailsQuery,
  useGetDocumentListQuery,
  useGetDocumentNotSearchableReasonQuery,
  useGetFeatureFlagsQuery,
  useGetFreshdeskFieldsQuery,
  useGetHelpdeskQuery,
  useGetIntercomFieldsQuery,
  useGetPaidPlanDetailsQuery,
  useGetPermissionsByRoleQuery,
  useGetPermissionsQuery,
  useGetProductMentionFieldsQuery,
  useGetWidgetConfigQuery,
  useLazyGetDocumentListQuery,
  useLazyGetFreshchatGroupsQuery,
  useRequestPlanTierUpgradeMutation,
} = dashboardApi;
